import { useCoreConfig, useGroupCoreConfig, useResolvedTarifDeckung } from '@eis/core';
import { deckungOf, type Deckung } from '../../../model/deckung';
import { groupDeckungOf, resolveGroupDeckung } from '../../../model/deckung-group';
import { assetOfErn, manifestRef, type Sources } from '../../../model/deckung-overview';

export type DeckungOwner = 'tarif' | 'group';

/** An asset a card or a link opens: the owner's asset route plus the asset's name. */
export interface DeckungAssetRef {
  readonly owner: DeckungOwner;
  readonly slug: string;
  readonly name: string;
}

export interface VersionDeckung {
  /** `none`: the version pins no coverage document (or its child pins none). */
  readonly state: 'loading' | 'error' | 'none' | 'ready';
  readonly doc: Deckung | undefined;
  readonly sources: Sources | undefined;
  /** What a variant card opens: the tarif's document, or the group's override (else the child's). */
  readonly asset: DeckungAssetRef | undefined;
  readonly error: Error | null;
  readonly retry: () => void;
}

/**
 * The coverage document a version pins, as the preview shows it (ui-spec §10). Nothing is stored
 * twice: the cards are recomputed from the pinned asset on every read.
 *
 * - A PUBLISHED (active, retired) version is read through the service's own resolution
 *   (`GET /v1/tarif-deckung:resolve`), so a group version shows the RESOLVED document — allowed
 *   variants only, with each section's source.
 * - A DRAFT cannot be resolved (it has no number yet), so its pins are read directly: the tarif's
 *   pinned asset; for a group draft the child version's resolution with the draft's own override
 *   laid over it locally (`resolveGroupDeckung`, the service's merge).
 */
export function useVersionDeckung(
  owner: DeckungOwner,
  ern: string,
  manifest: unknown,
  draft: boolean,
): VersionDeckung {
  const pin = manifestRef(manifest, 'tarifDeckung');
  const pinned = assetOfErn(pin);
  const childErn = owner === 'group' ? manifestRef(manifest, 'childTarifVersion') : '';

  // Exactly one source per case; the others are disabled by an empty argument.
  const resolveArgs = !draft
    ? owner === 'tarif'
      ? { tarifVersionErn: ern }
      : { groupTarifVersionErn: ern }
    : owner === 'group'
      ? { tarifVersionErn: childErn }
      : {};
  const resolved = useResolvedTarifDeckung(resolveArgs);
  const tarifAsset = useCoreConfig(
    draft && owner === 'tarif' ? (pinned?.slug ?? '') : '',
    draft && owner === 'tarif' ? (pinned?.name ?? '') : '',
  );
  const groupAsset = useGroupCoreConfig(
    draft && owner === 'group' ? (pinned?.slug ?? '') : '',
    draft && owner === 'group' ? (pinned?.name ?? '') : '',
  );

  const retry = (): void => {
    void resolved.refetch();
    if (draft) void (owner === 'tarif' ? tarifAsset.refetch() : groupAsset.refetch());
  };
  const result = (
    state: VersionDeckung['state'],
    rest: Partial<VersionDeckung> = {},
  ): VersionDeckung => ({
    state,
    doc: undefined,
    sources: undefined,
    asset: undefined,
    error: null,
    retry,
    ...rest,
  });

  // A tarif draft: its pinned asset, read as it stands.
  if (owner === 'tarif' && draft) {
    if (pinned === undefined) return result('none');
    if (tarifAsset.isError) return result('error', { error: tarifAsset.error });
    if (tarifAsset.isPending) return result('loading');
    const config = tarifAsset.data.coreConfig?.config;
    return config === undefined
      ? result('none')
      : result('ready', { doc: deckungOf(config), asset: { owner: 'tarif', ...pinned } });
  }

  if (owner === 'group' && draft && childErn === '') return result('none');
  if (resolved.isError) return result('error', { error: resolved.error });
  if (
    resolved.isPending ||
    (owner === 'group' && draft && pinned !== undefined && groupAsset.isPending)
  )
    return result('loading');
  if (owner === 'group' && draft && groupAsset.isError)
    return result('error', { error: groupAsset.error });

  const config = resolved.data.config;
  if (config === undefined) return result('none');
  const childAsset = assetOfErn(resolved.data.tarifAssetErn ?? '');
  const groupAssetErn =
    owner === 'group' ? (draft ? pin : (resolved.data.groupAssetErn ?? '')) : '';
  const overrideAsset = assetOfErn(groupAssetErn);
  const asset: DeckungAssetRef | undefined =
    overrideAsset !== undefined
      ? { owner: 'group', ...overrideAsset }
      : childAsset !== undefined
        ? { owner: 'tarif', ...childAsset }
        : undefined;

  if (owner === 'group' && draft && pinned !== undefined) {
    // The draft's override over the child, merged here the way the service merges it.
    const merged = resolveGroupDeckung(
      deckungOf(config),
      groupDeckungOf(groupAsset.data?.coreConfig?.config),
    );
    return result('ready', { doc: merged.document, sources: merged.sources, asset });
  }
  return result('ready', { doc: deckungOf(config), sources: resolved.data.sources ?? {}, asset });
}
